import { useDispatch, useSelector } from 'react-redux';
import React, { useCallback } from 'react';
import Image from 'react-bootstrap/Image';
import { useTranslation } from 'react-i18next';
import { Badge, Box, Typography } from '@material-ui/core';
import ExpandMore from '@material-ui/icons/ExpandMore';
import { useToggle } from 'react-use';

import { getGridIsOpened } from 'reducers/UI/gridDisplay';
import { modalPages } from 'constants/modalPage';
import './Header.scss';
import { disableProductEditMode, enableProductEditMode } from 'actions/UI';
import { semanticVersion as version } from 'external_data';
import { openModalPage } from 'actions/modalPage';
import { useBreakpoints } from 'utils/hooks/UI';
import ErplyLogo from 'assets/img/erply-logo-white.svg';
import { getSelectedPos } from 'reducers/PointsOfSale';
import { getCanEditProducts } from 'reducers/Login';
import { useShortcut } from 'utils/hooks/keyboard/useShortcut';
import { getIsEditMode } from 'reducers/UI/productEditMode';
import { PluginComponent } from 'plugins';
import { HeaderInstaller } from 'containers/Header/components/Installer';
import { LogoutOnInactivity } from 'containers/Header/components/Inactivity/LogoutOnInactivity';
import {
  getEnabledPOSVersionControl,
  getSetting,
} from 'reducers/configs/settings';
import { createDistributedState } from 'utils/DistributedState';

import BottomPopup from '../BottomPopup';
import { VersionControlContainer } from '../VersionControl';

import {
  BackOfficeRedirect,
  Feedback,
  HeaderCloudSync,
  HeaderConnectivity,
  HeaderLink,
  HeaderNotifications,
  HeaderUserMenu,
  ProductEditMode,
  Settings,
  ViewChanger,
  SseHeaderComponent,
} from './components';
import useUpdateSettingInLS from './useUpdateSettingInLS';
import { useDeviceInfoCollectionConsentPopup } from './useDeviceInfoCollectionConsentPopup';

const {
  useHostState: useNotificationsTotal,
  useChildState: useNotificationsCount,
} = createDistributedState(counts => counts.reduce((a, b) => a + b, 0));
// TODO: Figure out how to get this JSDoc to correctly attach to the export
/**
 * Call this hook from any header component that wants to display a notification count in mobile view
 * (where header icons are hidden behind a collapse)
 *
 * In non-mobile view has no effect. Your component should still render its own notification badge too!
 * @example
 * function MyHeaderIcon() {
 *   const {pendingEvents} = useMyStuff()
 *
 *   useHeaderNotificationCount(pendingEvents.length)
 *
 *   return (
 *     <Badge value={pendingEvents.length}>
 *       <TheIcon onClick={stuff}/>
 *     </Badge>
 *   )
 * }
 */
export { useNotificationsCount };

const Header = () => {
  const gridDisplayOpened = useSelector(getGridIsOpened);

  const { t } = useTranslation('header');

  const dispatch = useDispatch();
  const isEditMode = useSelector(getIsEditMode);
  const selectedPos = useSelector(getSelectedPos);
  const rightToEdit = useSelector(getCanEditProducts);

  const sseEnabled = useSelector(getSetting('brazil_enable_sse'));
  const versionControlEnabled = useSelector(getEnabledPOSVersionControl);
  const isWrapper = 'wrapperInfo' in window;

  useUpdateSettingInLS('hideSignUpButton', 'hideSignUpButton');
  useUpdateSettingInLS('hideLoginAppButton', 'hideLoginAppButton');
  useUpdateSettingInLS('touchpos_app_background_url', 'loginBackgroundUrl');

  useDeviceInfoCollectionConsentPopup();

  const bp = useBreakpoints();
  const openCompanySettings = useCallback(() => {
    dispatch(openModalPage({ component: modalPages.Settings }));
    dispatch(openModalPage({ component: modalPages.companySettings }));
  }, [dispatch]);

  useShortcut(
    'confirm',
    isEditMode && (() => dispatch(disableProductEditMode())),
    30,
  );

  // prettier-ignore
  const separator = bp.sm ? (
    <li style={{ padding: 2 }}>
      <span style={{ display: "block", transform: "scale(1,2)", color: "gray", paddingBottom: 3 }}>
        |
      </span>
    </li>
  ) : null;
  return (
    <HeaderAreas
      override={
        isEditMode ? (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            {bp.mdplus && <Image className="logo" src={ErplyLogo} />}
            <div className="text--yellow font-weight-bold pr-3 pr-md-0 erply-header__announcement">
              {t('editModeDescription')}
            </div>
            <div
              className="btn font-weight-bold px-4 erply-header__edit-close"
              onClick={() =>
                isEditMode
                  ? dispatch(disableProductEditMode())
                  : dispatch(enableProductEditMode())
              }
            >
              {t('common:done')}
            </div>
          </Box>
        ) : null
      }
      logo={<Image className="logo" src={ErplyLogo} />}
      pos={
        <HeaderLink
          data-testid="header-location"
          className="text--yellow font-weight-bold pl-3 pl-lg-0 d-block"
          href="#"
          content={`${selectedPos.warehouseName} - ${selectedPos.name} - v${version}`}
          onClick={openCompanySettings}
        />
      }
      plugins={<PluginComponent hookname="ComponentHeader" />}
      icons={
        <ul>
          <PluginComponent hookname="UIHeaderIcons">
            {Boolean(sseEnabled) && (
              <SseHeaderComponent render={jsx => <li>{jsx}</li>} />
            )}
            {bp.sm && (
              <li>
                <HeaderNotifications />
              </li>
            )}
            {Boolean(versionControlEnabled) && (
              <li>
                <VersionControlContainer />
              </li>
            )}
            <li>
              <Feedback />
            </li>
            <li>
              <HeaderCloudSync />
            </li>
            {bp.mdplus && !isWrapper ? (
              <li>
                <HeaderInstaller />
              </li>
            ) : null}
            <li>
              <HeaderConnectivity />
            </li>
            {separator}
            {rightToEdit && gridDisplayOpened && bp.mdplus ? (
              <li>
                <ProductEditMode />
              </li>
            ) : null}
            {separator}
            <li>
              <BackOfficeRedirect />
            </li>
            {bp.mdplus ? (
              <li>
                <ViewChanger />
              </li>
            ) : null}
            <li>
              <Settings />
            </li>
          </PluginComponent>
        </ul>
      }
      user={
        <>
          {/* Visible only in debug mode, but always active */}
          <LogoutOnInactivity />
          <HeaderUserMenu />
        </>
      }
      mobileActions={<BottomPopup />}
    />
  );
};

/**
 * Implements the layout of the header, exposes slots as props
 * @param logo JSX to put in the logo slot
 * @param pos JSX to put in the pos name slot (pos name + version)
 * @param user JSX to put in the usermenu slot (username + dropdown menu)
 * @param mobileActions JSX to put in the mobileActions slot (dropdown menu for mobile view)
 * @param icons JSX to put in the icons slot (POS core header icons)
 * @param plugins JSX to put in the plugins slot (Plugin header icons)
 * @param override JSX to put over the entire first row (f.ex. edit mode banner)
 * @see ./Header.scss for a description of the layout itself
 */
function HeaderAreas({
  logo,
  pos,
  user,
  mobileActions,
  icons,
  plugins,
  override,
}) {
  const [open, toggleOpen] = useToggle(false);

  const bp = useBreakpoints();
  const bpName = (function getBreakpointName() {
    if (!bp.sm) {
      return 'xs';
    }
    if (!bp.md) {
      return 'sm';
    }
    return 'md';
  })();

  const iconNotifications = useNotificationsTotal();
  const className = `layout content ${open ? 'expanded' : ''}`;
  return (
    <Box className={className} data-bp={bpName}>
      {override && <Box data-area="override">{override}</Box>}
      <Box data-area="logo">{logo}</Box>
      <Box data-area="pos">{pos}</Box>
      <Box data-area="plugins">
        <iconNotifications.Provider>{plugins}</iconNotifications.Provider>
      </Box>
      <Box data-area="icons">
        <iconNotifications.Provider>{icons}</iconNotifications.Provider>
      </Box>
      <Box data-area="userMenu">{user}</Box>
      <Box data-area="mobileMenu">{mobileActions}</Box>
      <Box data-area="dropdown">
        <Badge badgeContent={iconNotifications.summary} color="primary">
          <Typography onClick={() => toggleOpen()}>
            <ExpandMore className="flip" />
          </Typography>
        </Badge>
      </Box>

      {/* Empty box can be used for flex layouts */}
      <Box data-area="blank" />
    </Box>
  );
}
export default Header;
